import React, { useMemo } from 'react';
import { Col, Empty, Row, Skeleton, Statistic } from 'antd';
import { Doughnut } from 'react-chartjs-2';
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
} from 'chart.js';
import { useTheme } from '../../../context/ThemeContext';
import { METRIC_LABELS } from '../constants';
import { formatCount, formatPercentage, percentOf } from '../utils/formatters';
import { SectionCard, SectionTitle } from '../Styles';
import type { CustomerMix } from '../types';

ChartJS.register(ArcElement, Tooltip, Legend);

/** New / returning / unknown, in that order. */
const SEGMENT_COLORS = ['#2D6CCA', '#52c41a', '#bfbfbf'];
const SEGMENT_HOVER = ['#1d5bb8', '#3fa513', '#a6a6a6'];

interface CustomerMixCardProps {
  customers?: CustomerMix;
  loading?: boolean;
}

/**
 * New-vs-returning customer mix — the headline metric of the creator detail screen.
 *
 * Deliberately given a full card with a ratio chart rather than a row in a table:
 * "is this creator bringing us new customers or just discounting our existing ones"
 * is the question this whole report exists to answer.
 *
 * `customers.unknown` covers orders the backend couldn't phone-match either way, so
 * it's shown as its own muted segment rather than folded into one of the other two.
 */
const CustomerMixCard: React.FC<CustomerMixCardProps> = ({ customers, loading }) => {
  const { isDarkMode } = useTheme();

  const newCount = customers?.new ?? 0;
  const returningCount = customers?.returning ?? 0;
  const unknownCount = customers?.unknown ?? 0;
  const total = newCount + returningCount + unknownCount;

  const chartData = useMemo(
    () => ({
      labels: [
        METRIC_LABELS.NEW_CUSTOMERS,
        METRIC_LABELS.RETURNING_CUSTOMERS,
        METRIC_LABELS.UNKNOWN_CUSTOMERS,
      ],
      datasets: [
        {
          data: [newCount, returningCount, unknownCount],
          backgroundColor: SEGMENT_COLORS,
          hoverBackgroundColor: SEGMENT_HOVER,
          borderWidth: 0,
        },
      ],
    }),
    [newCount, returningCount, unknownCount]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '62%',
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            boxWidth: 12,
            padding: 12,
            color: isDarkMode ? '#c9d1d9' : '#4e5156',
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const value = context.raw ?? 0;
              const sum = context.dataset.data.reduce(
                (a: number, b: number) => a + b,
                0
              );
              const pct = sum > 0 ? ((value / sum) * 100).toFixed(1) : '0.0';
              return `${context.label}: ${value.toLocaleString()} (${pct}%)`;
            },
          },
        },
      },
    }),
    [isDarkMode]
  );

  return (
    <SectionCard>
      <SectionTitle>{METRIC_LABELS.CUSTOMER_MIX}</SectionTitle>

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : total === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No attributed customers in this range"
        />
      ) : (
        <Row gutter={[24, 16]} align="middle" style={{ marginTop: 12 }}>
          <Col xs={24} md={10}>
            <div style={{ height: 220, position: 'relative' }}>
              <Doughnut data={chartData} options={chartOptions} />
            </div>
          </Col>

          <Col xs={24} md={14}>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={8}>
                <Statistic
                  title={METRIC_LABELS.NEW_CUSTOMERS}
                  value={formatCount(newCount)}
                  styles={{ content: { color: SEGMENT_COLORS[0] } }}
                />
                <span style={{ fontSize: 12, color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                  {formatPercentage(percentOf(newCount, total))} of customers
                </span>
              </Col>
              <Col xs={12} sm={8}>
                <Statistic
                  title={METRIC_LABELS.RETURNING_CUSTOMERS}
                  value={formatCount(returningCount)}
                  styles={{ content: { color: SEGMENT_COLORS[1] } }}
                />
                <span style={{ fontSize: 12, color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                  {formatPercentage(percentOf(returningCount, total))} of customers
                </span>
              </Col>
              <Col xs={12} sm={8}>
                <Statistic
                  title={METRIC_LABELS.UNKNOWN_CUSTOMERS}
                  value={formatCount(unknownCount)}
                  styles={{ content: { color: SEGMENT_COLORS[2] } }}
                />
                <span style={{ fontSize: 12, color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                  Not phone-matched
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </SectionCard>
  );
};

/**
 * Memoised: re-renders only when its own props change, not whenever the parent
 * screen re-renders for an unrelated filter or fetch.
 */
export default React.memo(CustomerMixCard);
